<script setup>
import { computed, watch } from 'vue';

import { DEFAULT_QUERY_PARAMS } from '@pfsc/core/models/query-params';
import { globalTranslate } from '../locale/app.i18n';
import AppPagination from './app-pagination.vue';
import { useRoute, useRouter } from 'vue-router';
import { useTriggerStore } from '@pfsc/core/stores/trigger.store';
import { ref } from 'vue';
import { useTabsManagementStore } from '@pfsc/core/stores/tabs-management.store';
import { storeToRefs } from 'pinia';

const SELECT_ROW_COLUMN_KEY = 'data-table-select';

const props = defineProps({
  mobileCellColumnKey: { type: String, default: 'mobile-cell' },
  modelValue: { type: Array, default: () => [] },
  headers: {
    /** @type {import("vue").PropType<Array<{ title: string, key: string }>>} */
    type: /** @type {import("vue").PropType<Array<{ title: string, key: string }>>} */ (Array),
    default: () => [],
  },
  items: { type: Array, default: () => [] },
  page: { type: Number, default: DEFAULT_QUERY_PARAMS.page },
  perPage: { type: Number, default: DEFAULT_QUERY_PARAMS.perPage },
  sort: { type: Object, default: () => ({}) },
  totalResult: { type: Number, default: 0 },
  totalPages: { type: Number, default: 0 },
  isItemsLoading: { type: Boolean, default: false },
  fetchItems: { type: Function, default: () => {} },
  itemKey: { type: String, default: 'id' },
  canRowToggle: { type: Boolean, default: false },
  isCheckboxShown: { type: Boolean, default: true },
  cellTitle: { type: String, default: '' },
});

const emits = defineEmits(['on-page-change', 'update:model-value', 'row-selected']);

const tabsManagementStore = useTabsManagementStore();
const { currentTab } = storeToRefs(tabsManagementStore);
const route = useRoute();
const router = useRouter();
const trigger = useTriggerStore();

const selectedRow = ref(null);
const selectedId = ref(null);

const isActiveRow = item => computed(() => selectedRow?.value?.id === item.raw.id);

const tableHeaders = computed(() => [
  {
    title: props.cellTitle || globalTranslate('assignment.learnerInfo'),
    key: props.mobileCellColumnKey,
    sortable: false,
    minWidth: '280px',
  },
]);

const isSelectAllRow = computed(() => props.items.length > 0
  && props.items.every(item => props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey])));

const isSelectIndeterminate = computed(() => {
  const selectedInItems = props.items.filter(item => props.modelValue.some(
    selected => selected[props.itemKey] === item[props.itemKey],
  ));
  return selectedInItems.length > 0 && selectedInItems.length < props.items.length;
});

/**
 * Check if an item is selected.
 * @param {object} item Item to check.
 * @returns {boolean}
 */
function isItemSelected(item) {
  return props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey]);
}

/**
 * Toggle selection for a single item.
 * @param {object} item Item to toggle.
 */
function toggleItemSelection(item) {
  const isSelected = isItemSelected(item);
  const nextSelection = isSelected
    ? props.modelValue.filter(s => s[props.itemKey] !== item[props.itemKey])
    : [...props.modelValue, item];
  emits('update:model-value', nextSelection);
}

/**
 * Handle select all row.
 * @param {boolean} isChecked Checkbox event.
 */
function onSelectAllRow(isChecked) {
  const currentItemIds = new Set(props.items.map(i => i[props.itemKey]));
  const nextSelection = isChecked
    ? [...props.modelValue, ...props.items.filter(item => !isItemSelected(item))]
    : props.modelValue.filter(s => !currentItemIds.has(s[props.itemKey]));
  emits('update:model-value', nextSelection);
}

/**
 * Handle page changes.
 * @param {object} options New page.
 */
function onPageChange(options) {
  emits('on-page-change', options);
  if (options.perPage !== props.perPage || options.page !== props.page) {
    props.fetchItems(props.sort);
  }
}

/**
 * Update selectedRow and selectedId based on table row selected
 * @param {object} row Select row
 */
function selectRow(row) {
  selectedRow.value = (row == null || selectedRow.value?.id === row.id) ? null : row;
  selectedId.value = selectedRow?.value ? selectedRow?.value?.id : null;
  const newQuery = {
    tab: currentTab?.value ?? null,
  };

  if (selectedId.value) {
    newQuery.confirmationId = selectedId.value;
  }
  router.push({ query: newQuery });
  emits('row-selected', selectedRow.value);
}

/**
 * Toggle selection state for a row when allowed.
 * @param {object} row Row wrapper from the table slot.
 * @returns {void}
 */
function toggleRow(row) {
  if (props.canRowToggle) {
    selectRow(row.raw);
  }
}

/**
 * Clear selected row from trigger
 */
watch(
  () => trigger.clearSelectedRowChange,
  () => {
    selectedRow.value = null;
    router?.push({ query: { tab: currentTab?.value ?? null } });
  },
);

/**
 * Update selectedId based on confirmationId query param
 */
watch(() => route?.query?.confirmationId, async newValue => {
  selectedId.value = newValue ? Number(newValue) : null;
}, { immediate: true });

/**
 * Set selectedRow once data is done loading
 */
watch(
  () => props.isItemsLoading,
  isLoading => {
    if (!isLoading) {
      selectedRow.value = props.items.find(itm => itm.id === selectedId.value);
      selectedId.value = null;

      if (selectedRow.value) {
        emits('row-selected', selectedRow.value);
      }
    }
  },
);
</script>

<template>
  <v-data-table-server
    :model-value="modelValue"
    :page="page"
    :item-per-page="perPage"
    :items="items"
    :items-length="totalResult"
    :headers="tableHeaders"
    :loading="isItemsLoading"
    :item-key="itemKey"
    :item-value="itemKey"
    :select-all="isCheckboxShown"
    return-object
    hide-default-footer
    fixed-header
    :no-data-text="globalTranslate('table.noDataText')"
    :class="$style.table"
  >
    <template #headers="{ columns }">
      <tr>
        <td v-if="isCheckboxShown" :class="[$style.checkboxHeader, $style.header]">
          <v-checkbox-btn
            :model-value="isSelectAllRow"
            :indeterminate="isSelectIndeterminate"
            :disabled="isItemsLoading"
            @update:model-value="onSelectAllRow"
          />
        </td>
        <template v-for="column in columns" :key="column.key">
          <td
            v-if="column.key !== SELECT_ROW_COLUMN_KEY"
            :class="$style.header"
          >
            {{ column.title }}
          </td>
        </template>
      </tr>
    </template>

    <template #item="{ internalItem: item }">
      <tr
        :class="[
          $style.courseRow,
          isActiveRow(item).value && $style.activeRow,
          canRowToggle && $style.pointerCursor
        ]"
        @click="toggleRow(item)"
      >
        <td v-if="isCheckboxShown" :class="$style.checkboxHeader">
          <v-checkbox
            :class="$style.checkbox"
            :model-value="isItemSelected(item.raw)"
            :disabled="isItemsLoading"
            @update:model-value="toggleItemSelection(item.raw)"
            @click.stop
          />
        </td>
        <td>
          <slot :name="`item.${props.mobileCellColumnKey}`" :item="item">
            <div :class="$style.mobileCell">
              <template v-for="column in headers" :key="column.key">
                <div :class="$style.mobileLine">
                  <span :class="$style.mobileLabel">{{ column.title }}:</span>
                  <slot
                    :name="column.key"
                    :item="item"
                    :column="column"
                  >
                    <span>{{ item.columns[column.key] }}</span>
                  </slot>
                </div>
              </template>
            </div>
          </slot>
        </td>
      </tr>
    </template>

    <template #bottom>
      <app-pagination
        :page="page"
        :per-page="perPage"
        :total="totalResult"
        :total-pages="totalPages"
        :disabled="isItemsLoading"
        @on-page-change="onPageChange"
      />
    </template>
  </v-data-table-server>
</template>

<style module lang="scss">
.table {
  font-size: var(--font-size-md);
}

.header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-weight: 600;
  background: white;
}

.activeRow {
  background-color: var(--table-cell-background-color-darker);
}

.mobileCell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  font-size: 14px;
}

.mobileLine {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.mobileLabel {
  font-weight: 600;
  width: 125px;
}

.checkboxHeader {
  width: 48px;
}

.checkbox {
  display: flex;

  i {
    font-size: 19.5px;
  }
}
</style>
